import React from 'react';
import { ModalController } from '../../../../components/Modal/ControlledModal';
import { useChanges, Form } from '../../../../components/Form/Form';
import { Address } from '../../../../model/company';
import { TextField } from '../../../../components/Form/TextField/TextField';
import { useRemoteData } from '../../../../model/data';
import { getCountries } from '../../../../model/country';
import { Select } from '../../../../components/Form/Select/Select';
import { sortBy } from 'lodash';
import { handleError } from '../../../../model/notification';
import { updateDelivery, Delivery, deliveryNoteTypes, DeliveryNoteType } from '../../../../model/delivery';
import { Order } from '../../../../model/order';
import { t } from '../../../../locales/translate';
import { Label } from '../../../../components/Form/Label/Label';
import { AsyncSearch } from '../../../../components/Form/AsyncSearch/AsyncSearch';
import { uniqBy } from 'lodash';
import { Stack } from '../../../../components/Stack/Stack';
import { Spacer } from '../../../../components/Spacer/Spacer';

interface Props {
  delivery: Delivery & { order: Order };
  controller: ModalController<Address>;
  reduced?: boolean;
  onSubmit: () => void;
}

export function EditDelivery({ delivery, controller, onSubmit, reduced }: Props): JSX.Element {
  const [changes, , model, trackChanges] = useChanges<
    Address & { trackingCode?: string; shippingMethodId?: string; deliveryNoteType: DeliveryNoteType }
  >({
    ...controller.model,
    trackingCode: delivery.trackingCode,
    shippingMethodId: delivery.shippingMethodId,
    deliveryNoteType: delivery.deliveryNoteType,
  });

  const [countries] = useRemoteData(getCountries, (db) => {
    return uniqBy(db.table('countries').all, 'countryCode');
  });

  async function handleOnSubmit(): Promise<void> {
    const { trackingCode, shippingMethodId, deliveryNoteType, ...address } = {
      ...model,
      ...changes,
    };

    try {
      await updateDelivery(delivery.id, {
        orderId: delivery.order.id,
        address,
        shippingMethodId,
        trackingCode: trackingCode || delivery.trackingCode,
        deliveryNoteType,
      });
      onSubmit();
      controller.close();
    } catch (error) {
      handleError(error);
    }
  }

  return (
    <Form model={model} onInput={trackChanges}>
      {(form) => {
        controller.configure({
          onSubmit: handleOnSubmit,
          canSubmit: form.valid,
        });

        return (
          <Stack>
            {!reduced && (
              <>
                <TextField name="firstName" label={t.model.user.firstName} />
                <TextField name="lastName" label={t.model.user.lastName} />
                <TextField name="company" label={t.model.company._name} />
                <Spacer size="none" />
                <Label title={t.model.address._name} />
                <TextField name="address" label={t.model.address.street} />
                <TextField name="address2" label={t.model.address.additionalStreet} />
                <TextField name="postCode" label={t.model.address.postCode} />
                <TextField name="city" label={t.model.address.city} />
                <Select
                  name="countryCode"
                  label={t.model.address.country}
                  defaultValue={model.countryCode}
                  options={sortBy(countries, 'name').map((c) => ({
                    label: c.name,
                    value: c.countryCode,
                  }))}
                  allowEmpty
                />
                <Spacer size="none" />
                <Label title={t.model.delivery.delivery} />
                <Select
                  name="deliveryNoteType"
                  label={t.model.delivery.deliveryNoteType}
                  defaultValue={model.deliveryNoteType}
                  options={deliveryNoteTypes.map((deliveryNoteType) => ({
                    label: t.model.delivery.deliveryNoteTypes[deliveryNoteType],
                    value: deliveryNoteType,
                  }))}
                  required
                />
                <Spacer size="none" />
                <Label title={t.model.delivery.shipping} />
                <AsyncSearch
                  label={t.model.delivery.shippingMethod}
                  name="shippingMethodId"
                  searchIn={['shipping-product']}
                  position="top"
                ></AsyncSearch>
              </>
            )}
            <TextField name="trackingCode" label={t.model.delivery.trackingCode} />
          </Stack>
        );
      }}
    </Form>
  );
}
